import React, { useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { bool, func, node, string, shape, any } from "prop-types";
import { getConditionallyRequiredProp } from "@lib/getConditionallyRequiredProp";

import { useIsInitialRender } from "@hooks/useIsInitialRender";
import CloseIcon from "./Icons/Close";
import { useTranslation } from "@lib/i18n";
import { useRouter } from "next/router";
import { twMerge } from "tailwind-merge";
import useGlobalScrollPrevent from "@hooks/useGlobalScrollPrevent";

const Modal = ({
  ariaLabel,
  children,
  className,
  disableBackdropClick = false,
  disableEscapePress = false,
  doNotRenderInvisibile = false,
  closeButtonDataId,
  firstElement,
  isOpen,
  lastElement,
  onClose,
  preventGoBack = false,
  withCloseIcon,
  ...props
}) => {
  const isInitialRender = useIsInitialRender();
  const closeIconRef = useRef(null);
  const router = useRouter();
  const { t } = useTranslation();
  const controlKeys = {
    TAB: 9,
  };

  useGlobalScrollPrevent(isOpen);

  // if CloseIcon is used - it will be the first element for tab navigation
  if (withCloseIcon) {
    firstElement = closeIconRef;
  }
  // if the last element is not set - the first element will be the last for tab navigation
  if (!lastElement) {
    lastElement = firstElement;
  }

  useEffect(() => {
    router.beforePopState(() => {
      if (isOpen && preventGoBack) {
        onClose();
        window.history.pushState(null, document.title, window.location.href);
        return false;
      }
      return true;
    });

    return () => {
      router.beforePopState(() => true);
    };
  }, [isOpen, preventGoBack, router]);

  useEffect(() => {
    if (isOpen) {
      if (firstElement && firstElement.current) firstElement.current.focus();
      document.onkeydown = event => {
        if (event.keyCode === controlKeys.TAB && firstElement && lastElement) {
          if (event.target.tagName === "BODY") {
            // if focus has been lost - set it to firstElement again
            event.preventDefault();
            firstElement.current.focus();
          } else if (event.target === firstElement.current && event.shiftKey) {
            event.preventDefault();
            lastElement.current.focus();
          } else if (event.target === lastElement.current && !event.shiftKey) {
            event.preventDefault();
            firstElement.current.focus();
          }
        }
      };
    } else {
      document.onkeydown = null;
    }
    return () => (document.onkeydown = null);
  }, [isOpen, closeIconRef.current]);

  useEffect(() => {
    const listener = event => {
      if (event.key === "Escape" && !disableEscapePress && isOpen) {
        onClose();
      }
    };

    if (!disableEscapePress && isOpen) {
      document.addEventListener("keydown", listener);
    }

    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [disableEscapePress, isOpen, onClose]);

  const onBackdropClick = () => {
    if (disableBackdropClick) {
      return;
    }

    onClose();
  };

  // Modals are attached directly to the document using
  // portals, the document object is not available on the
  // server, so they can't be rendered during SSR
  if (isInitialRender) {
    return null;
  }

  if (doNotRenderInvisibile && !isOpen) {
    return null;
  }

  return createPortal(
    <div
      className={twMerge(
        "justify-center items-center absolute inset-0",
        isOpen ? "flex" : "hidden"
      )}
    >
      <div
        className="max-sm:hidden bg-primaryDark/90 backdrop-blur-xs fixed inset-0 z-50"
        onClick={onBackdropClick}
      />
      <div className="grid place-items-center fixed inset-0 z-50 pointer-events-none">
        <div
          aria-label={ariaLabel}
          aria-modal="true"
          className={twMerge(
            "relative bg-white border border-primaryDark size-full max-w-full px-1 py-4 pointer-events-auto sm:rounded-xl sm:h-auto sm:w-160",
            "border-2 border-primaryLight bg-gradient-to-t from-primaryLight to-white",
            className
          )}
          {...props}
          role="dialog"
        >
          {withCloseIcon && (
            <button
              aria-label={t("basket.back")}
              className="
                size-8 grid place-items-center
                border-2 border-primaryOrange
                bg-white text-primaryOrange rounded-sm
                absolute right-3 top-3 z-10
                transition duration-150 cursor-pointer
                hover:bg-primaryOrange hover:text-white hover:scale-110 active:scale-95
              "
              onClick={onClose}
              ref={closeIconRef}
            >
              <CloseIcon data-id={closeButtonDataId} size="default" />
            </button>
          )}
          <div className="max-h-[calc(100dvh-2rem-4px)] sm:max-h-[80dvh] px-4 overflow-auto fancy-scrollbars">
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

Modal.propTypes = {
  ariaLabel: string.isRequired,
  children: node.isRequired,
  className: string,
  closeButtonDataId: string,
  disableBackdropClick: bool,
  disableEscapePress: bool,
  doNotRenderInvisibile: bool,
  firstElement: shape({ current: any }),
  isOpen: bool.isRequired,
  lastElement: shape({ current: any }),
  onClose: getConditionallyRequiredProp(func, props => props.withCloseIcon),
  preventGoBack: bool,
  withCloseIcon: bool,
};

export default Modal;
